import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { AppState } from '../app-state';
import { RoleMgr } from '../../core';

import { TestSeq, Stage } from '../models';
import { TestSeqDoc, testSeqMethods, testSeqSchema, testSeqPullQueryBuilder, testSeqPushQueryBuilder} from '../schema/test-seq-schema';
import { AuthenticationService } from './authentication.service';
import { TestbedRxdbState } from './testbed-rxdb/testbed-rxdb-state';

import { PostProcessingService } from './post-processing.service';
import { TestbedRxdbGraphQlCollectionService } from './testbed-rxdb/testbed-rxdb-graphql-collection.service';
import { RxGraphQLReplicationQueryBuilder, SyncOptionsGraphQL } from "rxdb";
import { DialerTestService } from 'dialer';
import { StageService } from './stage.service';
import { RestClient } from './rest-client';
import { HttpClient } from '@angular/common/http';
import { TableViewQueryCriteria, TableViewQueryResponse } from '../../shared/components/table-view/table-view-component-base';
import { TestLog } from 'dialer/lib/core/model/test-log';

@Injectable({ providedIn: 'root' })
export class TestSeqService extends TestbedRxdbGraphQlCollectionService<TestSeq, TestSeqDoc> {


  importsEnabledSubscription: Subscription
  watchTestSeqsNotImportedSubscription: Subscription
  importsEnabled = false
  stages: Stage[] = []
  restClient: RestClient<TestSeq>

  constructor(
      appState: AppState,
      public roleMgr: RoleMgr,
      dbState: TestbedRxdbState,
      authService: AuthenticationService,
      public postProcessingService: PostProcessingService,
      public dialerTestService: DialerTestService,
      public stageService: StageService,
      http: HttpClient) {
    super('testseq', testSeqSchema, appState, dbState, authService)
    this.methods = testSeqMethods
    this.syncConfig = {
      push: true,
      pull: true,
      intervalMs: this.appState.settings.get('rxdb.collections.testseq.intervalMs', 0),
      retry: true,
      bGraphQL: true,
      query: testSeqPullQueryBuilder(),
      pushQuery: testSeqPushQueryBuilder(),
      graphQLSubscription:
        `subscription{
          testSeqUpdated{
            testseqs{
              seqid
            }
          }
        }`
    }
    // Disable until more info about sbox_fatal_memory_exceeded that occured
    // during scout-point-local-photo-url service compact()
    /*this.initialReplicationComplete$.pipe(
      filter(bComplete=>bComplete), first() ).subscribe( async () => {
      try {
        await this.rxCollection.pouch.compact()
        console.log('Testseq collection compacted')
      } catch ( error : any ) {
        console.error(error)
      }
    })
    */
    this.restClient = new RestClient<TestSeq>(http, 'testSeq', appState )
  }

  async getByIdViaRest( seqid: string ) {
    const queryResult = await this.findTestSeqsViaRest({filters: [{key:'seqid', value:seqid, mode: '='}]})
    let testSeq : TestSeq = queryResult?.rows?.[0]
    if ( testSeq?.dialer ) {
      let strDialerData = testSeq.dialer as any as string
      //TODO - remove this parse step when all testbed.testseq.dialer values have been changed to single json encoding
      if ( strDialerData?.startsWith('"') ) {
        strDialerData = JSON.parse(strDialerData)
      }
      testSeq.dialer = JSON.parse(strDialerData)
    }
    return testSeq
  }

  private async findTestSeqsViaRest( queryCriteria : TableViewQueryCriteria ) {
    try {
        const result : any = await this.restClient.http.get(
            `${this.restClient.urlPath}/find`,
            { params: {criteria: JSON.stringify(queryCriteria)} }).toPromise()
        const resp : TableViewQueryResponse<TestSeq> = result.data
        return resp
    } catch (error) {
        console.error('findViaRest() error: ' + error?.message )
        throw error
    }
  }

  buildSyncOptions() : SyncOptionsGraphQL {
    return {
        url: `${this.appState.settings.getGraphQLUrl()}`,
        headers: { Authorization: 'Bearer ' + this.authService.currentUserValue.token },
        waitForLeadership: true,
        pull: {
            queryBuilder: this.syncConfig.query as RxGraphQLReplicationQueryBuilder,
            modifier: (doc) => {
              doc.dialer = JSON.parse(doc.dialer)
              return doc
            }
        },
        push: {
            queryBuilder: this.syncConfig.pushQuery as RxGraphQLReplicationQueryBuilder,
            modifier: (doc) => {
              doc.dialer = JSON.stringify(doc.dialer)
              // doc._rev = doc.updatedAt + '-graphqlrevision'
              return doc
            }
        },
        deletedFlag: 'deleted',
        live:  this.syncConfig.intervalMs == 0,
        liveInterval: 60 * 1000,
        syncRevisions: false
    }
  }

  createTestSeqDocFromPiDbTestLog(
    testLog: TestLog,
    seqStatus: 'SEQ_FINISHED'|'DIALER_SEQ_FINISHED' //TODO add more status values as needed
  ) {
    const rootTest = testLog.data;
    const startDate = Date.parse(testLog.startTime)
    const endDate = isNaN(startDate) ? null : new Date(startDate + (rootTest.testDuration*1000.0))
    const endTime = endDate ? endDate.toISOString(): null

    const testSeqDoc = this.rxCollection.newDocument({
      seqid: testLog.seqId,
      pointId: testLog.pointId,
      testPointId: testLog.testPointId,
      importTime: null,
      dialer: rootTest,
      status: seqStatus,
      cycle: rootTest.cycle,
      stage: rootTest.stage,
      startTime: rootTest.startTime,
      endTime,
      testOrder: rootTest.testOrder,
      userId: rootTest.userId,
      groupingfield:null,
      // updatedAt: new Date().getUTCMilliseconds(),
      // _rev: new Date().getUTCMilliseconds() + 'graphqlrevision'
    });
    return testSeqDoc;
  }
}